/**
 * Entity Search Service - Universal entity search for follow-ups
 */

import api from "../../../shared/api.jsx";
import { logError, logInfo, logWarn, logDebug } from "../../../shared/utils/logger.js";

/**
 * Search entities (quotes, customers, invoices, orders, leads)
 * @param {string} searchTerm - Search term
 * @param {Object} [options] - Search options
 * @param {number} [options.limitPerType] - Limit per entity type
 * @param {number} [options.maxTotal] - Maximum total results
 * @returns {Promise<Array>} Search results
 */
export const searchEntities = async (searchTerm, options = {}) => {
  try {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }

    const params = new URLSearchParams({ q: searchTerm.trim() });
    if (options.limitPerType) {
      params.append("limitPerType", options.limitPerType);
    }
    if (options.maxTotal) {
      params.append("maxTotal", options.maxTotal);
    }

    const url = `/followups/search/entities?${params.toString()}`;
    logDebug("Search service calling", { url, searchTerm, options });

    // Add timeout to prevent infinite loading
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Search timeout after 10 seconds")), 10000);
    });

    const searchPromise = api.get(url);

    const response = await Promise.race([searchPromise, timeoutPromise]);

    logDebug("Search service raw response", { url, hasData: !!response?.data });

    const payload = response?.data ?? response ?? {};

    if (payload.success !== undefined) {
      const results = payload.results || payload.data || [];
      logDebug("Search service found results (success payload)", { 
        count: results.length,
        url 
      });
      return results;
    }

    if (Array.isArray(payload)) {
      logDebug("Search service found results (array payload)", { 
        count: payload.length,
        url 
      });
      return payload;
    }

    if (payload.results) {
      logDebug("Search service found results (results property)", { 
        count: payload.results.length,
        url 
      });
      return payload.results;
    }

    logWarn("Search service unexpected response format", { 
      url,
      payload: typeof payload === 'object' ? Object.keys(payload) : typeof payload 
    });
    return [];
  } catch (error) {
    logError("Search service error searching entities", {
      searchTerm,
      options,
      error: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    // Return empty array on any error to prevent infinite loading
    // This is acceptable for search as empty results are valid
    return [];
  }
};

